import { useEffect, useRef, type ReactElement } from 'react';
import {
  barsUpTo,
  hasServerBars,
  barsFor,
  modeOf,
  nextTimeBefore,
  primeServerBars,
  SERVER_HISTORY_REQUIRED,
  subscribeBars,
  unsubscribeBars,
} from './chart-adapter';

/** React wrappers for the custom elements the design package defines (`<x-import>` in the reference).
 *
 *  These exist because of one incompatibility, and it is worth stating plainly rather than discovering twice.
 *
 *  **React 19 sets props on a custom element as *properties*, not attributes**, whenever the name exists on the
 *  element — and `PFTvChart` declares `mode`, `sym`, `res` and `libPath` as getters with no setter. So rendering
 *  `<pf-tv-chart mode="trade">` directly makes React execute `el.mode = 'trade'`, which throws
 *  `Cannot set property mode of #<PFTvChart> which has only a getter`, and the whole screen unmounts. TradeHub,
 *  Replay and EOD rendered blank.
 *
 *  The element's own contract is attributes: it declares `observedAttributes` and re-targets the widget from
 *  `attributeChangedCallback`. So the wrapper renders the bare tag and writes every value with `setAttribute`,
 *  which is what the reference's runtime does and what the element is written against.
 *
 *  The rendered DOM is identical to the reference's — the tag, the attributes, the values. Nothing about the
 *  wrapper is visible in the output, which is the only reason it is acceptable under a DOM-parity rule.
 */

export interface CustomElementProps {
  readonly tag: string;
  /** Attribute name → value. `undefined` and `''` remove the attribute, matching how the reference omits an unset
   *  draft level rather than writing an empty one. */
  readonly attrs: Readonly<Record<string, string | undefined>>;
  readonly style?: React.CSSProperties;
  readonly className?: string;
  /** Handed out so a caller can listen to the element's own events. */
  readonly elementRef?: React.MutableRefObject<HTMLElement | null>;
}

/** Renders a custom element and drives it by attribute.
 *
 *  The element is **created imperatively and configured before it is connected**, then appended into a
 *  `display: contents` host. That shape is not incidental:
 *
 *   · A custom element reads its attributes in `connectedCallback`. `<pf-tv-chart>` reads `library-path` there to
 *     decide where to fetch the charting library from — so an attribute set afterwards, in an effect, arrives too
 *     late. The element fell back to its relative default, asked for `/execute/charting_library/…` on any nested
 *     route, got a 404, and cached that failure for the life of the page. Every chart stayed on its placeholder.
 *
 *   · React 19 sets a prop as a *property* when the name exists on the element, and this one declares `mode` as a
 *     getter with no setter — so the declarative form throws and unmounts the screen. Writing attributes ourselves
 *     avoids the question entirely.
 *
 *   · `display: contents` is the mounting shape the package expects; its own comment says as much. The host box
 *     contributes no layout, so the element participates in the parent's flex directly, exactly as the reference's
 *     `<x-import>` does.
 */
/** Never build a chart whose container has already been taken out of the document.
 *
 *  `<pf-tv-chart>` loads the charting library asynchronously and then builds:
 *
 *      loadLibrary(this.libPath).then(ok => { if (ok) this.build(); });
 *
 *  Nothing in that chain re-checks that the element is still connected. React's `StrictMode` double-invokes mount
 *  effects in development, so the sequence is: create element A, append it (its `connectedCallback` captures its
 *  mount and starts the library load) → cleanup removes A → create element B. When A's load resolves, A builds
 *  into a mount that is no longer in the document.
 *
 *  A detached container has no laid-out iframe, so the iframe has no `contentWindow`, and the library's own code is
 *
 *      ...contentWindow.document.write(n)... : console.warn("Unable to locate contentWindow for the created
 *      iframe. Please try disabling the `iframe_loading_compatibility_mode` featureset."),
 *      this._innerWindow().addEventListener("innerWindowLoad", ...)
 *
 *  — it warns that it could not reach the window and then dereferences that same null window anyway. The throw
 *  lands in a microtask, so it escapes the element's `try/catch` around the constructor and surfaces as an
 *  unhandled rejection: `Cannot read properties of null (reading 'addEventListener')`.
 *
 *  Measured rather than reasoned about. Instrumenting `build()` showed it called twice per mount:
 *
 *      { readyState: 'complete', mountConnected: false, mountW: 0,   mountH: 0   }   ← throws
 *      { readyState: 'complete', mountConnected: true,  mountW: 949, mountH: 888 }   ← the chart you see
 *
 *  The visible chart is the second call, which is why this was invisible on screen while taking six browser
 *  journeys down on their zero-console-errors assertions — including two of the six critical ones.
 *
 *  The guard is one condition and it is true in every case, not just under StrictMode: a build for a container that
 *  is gone has nothing to draw into and no one to draw for. Fast route changes and remounts hit the same path in
 *  production. It patches the element's own `build` rather than editing `apps/web/public/pf-tv-chart.js`, which is
 *  a verbatim copy of the package and stays that way (DEC-074).
 */
/** Tell the charting library where the stored history actually ends.
 *
 *  TradingView opens a chart at the current clock and asks the datafeed for bars there. The element answers
 *  `{ noData: true }` and nothing else — and a bare `noData` means *there is nothing at all for this symbol*, so
 *  the library stops asking and draws an empty pane. Stored history ends when the ledger ends, which for a seeded
 *  demo is not today, so every chart was blank on first paint.
 *
 *  The contract's answer is `nextTime`: the open of the most recent bar at or before the window asked for. The
 *  library then asks again there and finds the series. `/udf` returns the same field for the same reason; this is
 *  the browser half of one idea.
 *
 *  It decorates the datafeed the element builds rather than replacing it. The element owns the widget lifecycle,
 *  the resolution mapping and the subscription bookkeeping; reaching past it would mean re-implementing all three
 *  and diverging from the package at its next update. `apps/web/public/pf-tv-chart.js` stays a verbatim copy
 *  (DEC-088).
 */
type DatafeedBar = { time: number };
type BarsMeta = { noData?: boolean; nextTime?: number };
type GetBars = (
  symbolInfo: { name: string },
  resolution: string,
  periodParams: { from: number; to: number },
  onResult: (bars: DatafeedBar[], meta?: BarsMeta) => void,
  onError?: (reason: string) => void,
) => void;

function serveStoredHistory(el: HTMLElement): void {
  const proto = Object.getPrototypeOf(el) as {
    widgetOptions?: (this: unknown) => { datafeed?: { getBars?: GetBars } };
    __pfHistoryPatched?: boolean;
  };
  if (!proto || typeof proto.widgetOptions !== 'function' || proto.__pfHistoryPatched) return;
  const original = proto.widgetOptions;

  proto.widgetOptions = function patched(this: unknown) {
    // The patch is installed on the PROTOTYPE, once, so `this` is the element asking — not the one that happened to
    // be mounted first. Replay puts two charts on one screen in two different modes; capturing the element from the
    // enclosing scope would have given both of them the first one's mode.
    const self = this as HTMLElement;
    const options = original.call(this);
    const feed = options.datafeed;
    if (!feed || typeof feed.getBars !== 'function') return options;
    const inner = feed.getBars.bind(feed) as GetBars;

    feed.getBars = (symbolInfo, resolution, periodParams, onResult, onError) => {
      // Fail closed, and here rather than at the call sites. Every path that can retarget a chart — the attribute
      // effect below, the element's own `setSymbol`, a widget-initiated reload after a reconnect — arrives at this
      // one function, so this is the only place the guarantee can actually be made. Sequencing the callers is worth
      // doing (it removes the flash of the wrong series) but it is not the guarantee: a caller that forgets is a
      // caller that silently reintroduces invented candles.
      if (SERVER_HISTORY_REQUIRED && !hasServerBars(symbolInfo.name, resolution))
        return onResult([], { noData: true });

      // The pane's own mode, read off the element rather than passed in, because the widget can ask for bars long
      // after the React effect that mounted it has finished — and the attribute is the element's own state.
      const mode = modeOf(self.getAttribute('mode'));

      inner(
        symbolInfo,
        resolution,
        periodParams,
        (bars, meta) => {
          // A `backtest` pane is cut at its simulated clock. `barsUpTo` returns the bars unchanged for every other
          // mode, and null only for a series the adapter never primed — which the guard above has already refused.
          const cut = barsUpTo(symbolInfo.name, resolution, mode, bars);
          if (cut !== null && cut.length !== bars.length) return onResult(cut as DatafeedBar[], meta);
          if (bars.length > 0 || !meta?.noData) return onResult(bars, meta);
          const nextTime = nextTimeBefore(symbolInfo.name, resolution, periodParams.from * 1000);
          onResult([], nextTime === null ? { noData: true } : { noData: true, nextTime });
        },
        onError,
      );
    };

    // The subscription seam. The element's own datafeed does nothing here — the package's comment says
    // "historical study surface — no realtime channel by design" — and routing it through the adapter anyway is
    // what makes the adapter the single answer to "where could a tick enter a chart?". It admits none today, and
    // the day something publishes `prices:{symbol}` there is exactly one function to change.
    const feedWithSubs = feed as {
      subscribeBars?: (
        info: { name: string },
        resolution: string,
        onTick: (bar: DatafeedBar) => void,
        uid: string,
      ) => void;
      unsubscribeBars?: (uid: string) => void;
    };
    feedWithSubs.subscribeBars = (info, resolution, onTick, uid) => {
      subscribeBars(uid, info.name, resolution, modeOf(self.getAttribute('mode')), onTick);
    };
    feedWithSubs.unsubscribeBars = (uid) => {
      unsubscribeBars(uid);
    };

    return options;
  };
  proto.__pfHistoryPatched = true;
}

function guardDetachedBuild(el: HTMLElement): void {
  const proto = Object.getPrototypeOf(el) as {
    build?: (this: Record<string, unknown>) => void;
    __pfBuildGuarded?: boolean;
  };
  if (!proto || typeof proto.build !== 'function' || proto.__pfBuildGuarded) return;
  const original = proto.build;
  proto.build = function guarded(this: Record<string, unknown>) {
    const mount = this['_mount'];
    if (!(mount instanceof HTMLElement) || !mount.isConnected) return;
    return original.call(this);
  };
  proto.__pfBuildGuarded = true;
}

export function CustomElement({
  tag,
  attrs,
  style,
  className,
  elementRef,
}: CustomElementProps): ReactElement {
  const host = useRef<HTMLDivElement | null>(null);
  const own = useRef<HTMLElement | null>(null);
  const ref = elementRef ?? own;
  // Read inside the mount effect without making it a dependency: the initial attributes are wanted once, before
  // connection, and every later change is applied by the sync effect below.
  const initial = useRef(attrs);
  initial.current = attrs;

  useEffect(() => {
    const parent = host.current;
    if (!parent) return;
    let cancelled = false;
    const el = document.createElement(tag);
    for (const [name, value] of Object.entries(initial.current)) {
      if (value !== undefined && value !== '') el.setAttribute(name, value);
    }

    const connect = (): void => {
      if (cancelled) return;
      // Only now does `connectedCallback` run, with every attribute already in place.
      parent.appendChild(el);
      // After connection, so the element's prototype exists to patch, and before the library finishes loading and
      // the element calls `build()`.
      if (tag === 'pf-tv-chart') {
        guardDetachedBuild(el);
        serveStoredHistory(el);
      }
      ref.current = el;
    };

    if (tag === 'pf-tv-chart') {
      // The server's bars are registered *before* connection, because `connectedCallback` is where the element
      // decides what its datafeed will serve. Register afterwards and the first paint is the synthetic series, with
      // the real one arriving only on the next retarget — a chart that is briefly, invisibly wrong.
      // Connect either way. A failed fetch used to mean the element connected and drew invented bars; now the
      // datafeed refuses an unregistered series in server mode, so connecting on failure produces an empty chart
      // rather than a wrong one — and the element still exists, which is what lets it say so.
      void primeServerBars(
        initial.current['symbol'] ?? '',
        initial.current['interval'] ?? '',
        modeOf(initial.current['mode']),
      ).then(connect, connect);
    } else {
      connect();
    }

    return () => {
      cancelled = true;
      ref.current = null;
      el.remove();
    };
  }, [tag, ref]);

  // Which retarget is current. A trader clicking through four symbols starts four fetches, and only the last one
  // may write attributes — otherwise a slow response for the symbol they left lands after the fast one for the
  // symbol they are on, and the chart retargets backwards.
  const retarget = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const write = (names: readonly string[]): void => {
      for (const name of names) {
        const value = attrs[name];
        // Removed rather than set empty: the element reads `getAttribute('sl')` as a truthiness test for whether to
        // draw the order line, and `sl=""` would be a line at price zero.
        if (value === undefined || value === '') el.removeAttribute(name);
        else if (el.getAttribute(name) !== value) el.setAttribute(name, value);
      }
    };

    const symbol = attrs['symbol'];
    const interval = attrs['interval'];
    const retargeting =
      tag === 'pf-tv-chart' &&
      !!symbol &&
      !!interval &&
      (el.getAttribute('symbol') !== symbol || el.getAttribute('interval') !== interval);

    if (!retargeting) return write(Object.keys(attrs));

    // Everything except the two attributes that move the series can land now; those wait for the history, because
    // `attributeChangedCallback` retargets the widget the moment they are set and the datafeed is asked before the
    // fetch could possibly have resolved. That ordering is what made every symbol change after the first draw the
    // package's synthetic walk (ESC-008).
    write(Object.keys(attrs).filter((n) => n !== 'symbol' && n !== 'interval'));

    const generation = ++retarget.current;
    void primeServerBars(symbol, interval, modeOf(attrs['mode'])).then(() => {
      if (generation !== retarget.current || !ref.current) return;
      write(['symbol', 'interval']);
    });
  });

  return <div ref={host} style={{ display: 'contents', ...style }} className={className} />;
}

interface ChartApi {
  symbol?: () => string;
  setVisibleRange(range: { from: number; to: number }): void;
  createShape(
    point: { time: number; price?: number; channel?: string },
    options: {
      shape: string;
      lock?: boolean;
      disableSelection?: boolean;
      disableSave?: boolean;
      disableUndo?: boolean;
      text?: string;
      overrides?: Record<string, unknown>;
    },
  ): Promise<string> | string;
  removeEntity(id: string): void;
}
interface TvWidget {
  activeChart?: () => ChartApi;
  chart?: () => ChartApi;
  setSymbol?: (symbol: string, interval: string, done: () => void) => void;
  changeTheme?: (theme: 'Dark' | 'Light', options?: { disableUndo?: boolean }) => Promise<void>;
  applyOverrides?: (overrides: Record<string, unknown>) => void;
  save?: (done: (state: object) => void) => void;
  load?: (state: object) => Promise<void> | void;
  subscribe?: (event: string, cb: () => void) => void;
  unsubscribe?: (event: string, cb: () => void) => void;
}

/** Where a chart's layout is kept between visits, one slot per surface.
 *
 *  Per `mode` rather than one shared slot: the trade desk, the replay, the backtest and the EOD review are four
 *  different charts with four different jobs, and restoring the replay's drawings onto the live desk would be
 *  putting last week's annotations over today's price. */
const layoutKey = (mode: string): string => `pf.chart.layout.${mode || 'trade'}.v1`;

/** Reads a saved layout, or null when there is none or it cannot be trusted. */
function readLayout(mode: string): object | null {
  try {
    const raw = globalThis.localStorage?.getItem(layoutKey(mode));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as object) : null;
  } catch {
    // A layout saved by a different library version, or storage that is unavailable. A chart with default settings
    // is a working chart; a throw here would take the screen down.
    return null;
  }
}

function writeLayout(mode: string, state: object): void {
  try {
    globalThis.localStorage?.setItem(layoutKey(mode), JSON.stringify(state));
  } catch {
    // Quota, private browsing, storage disabled. Losing the layout is a worse session, not a broken one.
  }
}

/** The pane colours the package sets at construction from its initial theme. Re-applied on a theme change, because
 *  `changeTheme` moves the library's own palette and leaves these where they were — a dark chart on a white pane. */
function paneOverrides(dark: boolean): Record<string, unknown> {
  // The library's `overrides` API takes colour strings, not CSS custom properties: a `var(--surface)` handed to it
  // is drawn literally or ignored. Substituting our own token values would also be a visual deviation — the package
  // picked these for the chart pane specifically. All five are byte-for-byte from design-reference/pf-tv-chart.js.
  return {
    'paneProperties.background': dark ? '#101216' : '#ffffff', // token-lint-allow: package literal
    'paneProperties.backgroundType': 'solid',
    'paneProperties.vertGridProperties.color': dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', // token-lint-allow: package literal
    'paneProperties.horzGridProperties.color': dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', // token-lint-allow: package literal
    'scalesProperties.textColor': dark ? '#8b93a1' : '#5d6470', // token-lint-allow: package literal
  };
}

/** The chart API of a mounted element, or null while it is still loading. */
function chartOf(el: HTMLElement | null): ChartApi | null {
  if (!el) return null;
  const holder = el as unknown as { _tv?: TvWidget; _ready?: boolean };
  if (!holder._ready || !holder._tv) return null;
  return holder._tv.activeChart?.() ?? holder._tv.chart?.() ?? null;
}

/** Opens the chart on the data it actually has.
 *
 *  `PFMarketData` synthesises its series ending at the demo's own "today" (2026-07-24, the date the whole dataset is
 *  anchored to). The widget opens at the *real* clock, so on any later day it lands on an empty stretch of time and
 *  draws nothing — a blank pane that looks like a broken chart rather than a dataset that stops.
 *
 *  The datafeed could answer this itself by returning `nextTime` alongside `noData`, which is TradingView's own hint
 *  for "no bars here, the nearest ones are there" — but `getBars` lives in the verbatim package file. So the range is
 *  set once, here, when the chart reports ready. */
function showAvailableRange(el: HTMLElement, symbol: string, interval: string): void {
  const bars = barsFor(symbol, interval);
  if (!bars.length) return;
  const widget = (el as unknown as { _tv?: TvWidget })._tv;
  const chart = widget?.activeChart?.() ?? widget?.chart?.();
  if (!chart) return;
  const last = bars[bars.length - 1]!.time;
  // The final ~180 bars: enough context to read the trend, close enough in to see the candles.
  const first = bars[Math.max(0, bars.length - 180)]!.time;
  try {
    // TradingView's range is in seconds, not milliseconds.
    chart.setVisibleRange({ from: Math.round(first / 1000), to: Math.round(last / 1000) });
  } catch {
    // A range the widget will not accept leaves it where it was. Not worth taking the screen down for.
  }
}

/** A price level to draw, and what kind it is. */
interface Level {
  readonly price: number;
  readonly label: string;
  readonly colour: string;
  /** Dashed for a level under consideration, solid for one that is working. */
  readonly dashed: boolean;
}

interface Marker {
  readonly time: number;
  readonly price: number;
  readonly side?: string;
  readonly text?: string;
}

/** Draws the draft stop/target and the execution markers.
 *
 *  **Rebuilt on the Advanced Charts API.** The package's `syncLines()` and `syncMarkers()` call `createOrderLine()`
 *  and `createExecutionShape()`, and both throw *"only available on Trading Platform"* on this edition of the
 *  library — inside a `try {} catch {}` that swallows it, so the levels simply never appeared and nothing said why.
 *  `createShape` is the Advanced Charts equivalent and is what this uses.
 *
 *  Entities are tracked and removed before each redraw, or dragging the stop would leave a trail of old lines behind
 *  the new one. */
interface OverlayInput {
  /** Draft levels — what the trader is typing into the ticket. */
  sl?: string;
  tp?: string;
  slState?: string;
  tpState?: string;
  /** Committed levels — the stop and target of a position that exists. */
  slLive?: string;
  tpLive?: string;
  slTrailing?: string;
  entry?: string;
  side?: string;
  markers?: string;
}

// Shape colours reach the charting library, which resolves no custom properties — same constraint as the pane
// overrides above. RED and GREEN are the package's own (design-reference/pf-tv-chart.js); AMBER is the reference's
// own literal for --amb.
const RED = '#e2607a'; // token-lint-allow: package literal
const GREEN = '#4ec38a'; // token-lint-allow: package literal
/** A level on the wrong side of entry. Amber rather than red: it is a mistake, not a loss. */
const AMBER = '#e8b24a'; // token-lint-allow: reference literal

/** The levels to draw, draft and committed together.
 *
 *  A draft and a committed level can both exist at once — a trader holding a position and typing the next entry —
 *  so they are drawn together and told apart by weight: dashed for what is being considered, solid for what is
 *  actually working. Same colour, because a stop is a stop; different line, because one of them will lose you money
 *  and the other will not. */
function levelsOf(input: OverlayInput): Level[] {
  const out: Level[] = [];

  const draft = (raw: string | undefined, state: string | undefined, kind: 'SL' | 'TP'): void => {
    const price = Number(raw);
    if (!raw || !Number.isFinite(price) || price <= 0) return;
    const bad = state === 'invalid';
    out.push({
      price,
      // Named as wrong on the chart, not only in the ticket's error text. A stop drawn above entry on a buy is
      // obvious the moment you can see it against the candles.
      label: bad ? `DRAFT ${kind} · WRONG SIDE` : `DRAFT ${kind}`,
      colour: bad ? AMBER : kind === 'SL' ? RED : GREEN,
      dashed: true,
    });
  };
  draft(input.sl, input.slState, 'SL');
  draft(input.tp, input.tpState, 'TP');

  const live = (raw: string | undefined, kind: 'SL' | 'TP', trailing: boolean): void => {
    const price = Number(raw);
    if (!raw || !Number.isFinite(price) || price <= 0) return;
    out.push({
      price,
      label: trailing ? `${kind} · TRAILING` : kind,
      colour: kind === 'SL' ? RED : GREEN,
      dashed: false,
    });
  };
  live(input.slLive, 'SL', input.slTrailing === '1');
  live(input.tpLive, 'TP', false);

  return out;
}

async function drawOverlays(el: HTMLElement, chart: ChartApi, input: OverlayInput): Promise<void> {
  const holder = el as unknown as { __pfEntities?: string[] };
  for (const id of holder.__pfEntities ?? []) {
    try {
      chart.removeEntity(id);
    } catch {
      // An entity the chart already dropped (a symbol change clears them) is not an error.
    }
  }
  const ids: string[] = [];

  for (const lvl of levelsOf(input)) {
    try {
      const id = await chart.createShape(
        { time: 0, price: lvl.price, channel: 'close' },
        {
          shape: 'horizontal_line',
          // Locked and unselectable: these are a readout of the order desk, not a drawing. A trader who nudged one
          // would be editing a picture of their stop rather than their stop.
          lock: true,
          disableSelection: true,
          disableSave: true,
          disableUndo: true,
          overrides: {
            linecolor: lvl.colour,
            linestyle: lvl.dashed ? 2 : 0,
            linewidth: lvl.dashed ? 1 : 2,
            showLabel: true,
            text: lvl.label,
            textcolor: lvl.colour,
            horzLabelsAlign: 'right',
            vertLabelsAlign: 'bottom',
          },
        },
      );
      if (id) ids.push(id);
    } catch {
      // A level the chart will not take is left undrawn; the desk still shows the number.
    }
  }

  let markers: Marker[] = [];
  try {
    markers = JSON.parse(input.markers ?? '[]') as Marker[];
  } catch {
    markers = [];
  }
  for (const m of markers) {
    const short = /SHORT|SELL/i.test(m.side ?? '');
    try {
      const id = await chart.createShape(
        { time: Math.round(m.time / 1000), price: m.price },
        {
          shape: short ? 'arrow_down' : 'arrow_up',
          lock: true,
          disableSelection: true,
          disableSave: true,
          disableUndo: true,
          text: m.text ?? '',
          // token-lint-allow: same constraint as above — the library resolves no custom properties.
          overrides: { color: short ? RED : GREEN, fontsize: 10 },
        },
      );
      if (id) ids.push(id);
    } catch {
      // One marker the chart refuses does not cost the rest.
    }
  }
  holder.__pfEntities = ids;
}

/** `<pf-tv-chart>` — the TradingView Advanced Charts mount (`design-reference/pf-tv-chart.js`). */
export function PfTvChart(props: {
  symbol?: string;
  interval?: string;
  theme?: string;
  mode?: string;
  sl?: string;
  tp?: string;
  markers?: string;
  attribution?: string;
  'library-path'?: string;
  'min-height'?: string;
  /** Order-desk state, added by the transpiler's binding overlay. Not attributes on the element — the package's
   *  element knows nothing about them — but props this wrapper reads to decide what to draw. */
  slState?: string;
  tpState?: string;
  slLive?: string;
  tpLive?: string;
  slTrailing?: string;
  entry?: string;
  side?: string;
  style?: React.CSSProperties;
  className?: string;
}): ReactElement {
  const ref = useRef<HTMLElement | null>(null);
  // The desk props are consumed here and never written to the element: `<pf-tv-chart>` has no `slLive` attribute and
  // would carry it as dead markup that the reference does not have.
  const { style, className, slState, tpState, slLive, tpLive, slTrailing, entry, side, ...rest } = props;
  const symbol = props.symbol ?? 'XAUUSD';
  const interval = props.interval ?? '60';
  const mode = props.mode ?? 'trade';
  const dark = (props.theme ?? 'dark') !== 'light';
  const overlay: OverlayInput = {
    ...(props.sl === undefined ? {} : { sl: props.sl }),
    ...(props.tp === undefined ? {} : { tp: props.tp }),
    ...(slState === undefined ? {} : { slState }),
    ...(tpState === undefined ? {} : { tpState }),
    ...(slLive === undefined ? {} : { slLive }),
    ...(tpLive === undefined ? {} : { tpLive }),
    ...(slTrailing === undefined ? {} : { slTrailing }),
    ...(entry === undefined ? {} : { entry }),
    ...(side === undefined ? {} : { side }),
    ...(props.markers === undefined ? {} : { markers: props.markers }),
  };
  // One string, so the redraw effect fires exactly when something it draws has actually changed.
  const overlayKey = JSON.stringify(overlay);

  // Re-target the widget ourselves rather than through the element's `attributeChangedCallback`.
  //
  // **The element goes deaf after its first theme change.** `attributeChangedCallback` opens with
  // `if (!this._built …) return`, and `rebuild()` — which the first `theme` write triggers — calls `destroy()`,
  // which sets `_built = false`. `render()` never sets it back; only `connectedCallback` does, and the element is
  // already connected. So from that moment every attribute change is ignored, and picking a new instrument moved
  // the ticket and the desk while the chart carried on showing gold.
  //
  // Driving the widget directly also lets us do the part the element does not: TradingView drops drawings when the
  // symbol changes, so the levels have to be redrawn against the new series.
  useEffect(() => {
    const el = ref.current;
    const holder = el as unknown as { _tv?: TvWidget; _ready?: boolean } | null;
    if (!el || !holder?._ready || !holder._tv?.setSymbol) return;
    const chart = chartOf(el);
    if (chart && chart.symbol?.() === symbol) return;
    holder._tv.setSymbol(symbol, interval, () => {
      const entities = el as unknown as { __pfEntities?: string[] };
      // The chart cleared them on the way to the new symbol; forgetting them here stops the next redraw trying to
      // remove ids that no longer exist.
      entities.__pfEntities = [];
      showAvailableRange(el, symbol, interval);
      const next = chartOf(el);
      if (next) void drawOverlays(el, next, overlay);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overlay is rebuilt each render; overlayKey is its identity
  }, [symbol, interval, overlayKey]);

  // The chart follows the app's theme.
  //
  // The element's own theme handling calls `rebuild()`, which is both heavy — it throws the widget away and mounts a
  // new one, losing the trader's drawings and their place on the timescale — and dead, because `rebuild()` leaves
  // `_built` false and the callback that would trigger it returns early ever after. `changeTheme` re-palettes the
  // live widget in place, which is what a theme toggle should do.
  //
  // The pane colours are re-applied afterwards because the package set them at construction from whatever theme it
  // mounted with; `changeTheme` moves the library's palette and leaves those overrides behind, which is how you get
  // a dark chart drawn on a white pane.
  useEffect(() => {
    const el = ref.current;
    const widget = el ? ((el as unknown as { _tv?: TvWidget })._tv ?? null) : null;
    const ready = el ? (el as unknown as { _ready?: boolean })._ready : false;
    if (!el || !widget?.changeTheme || !ready) return;
    void Promise.resolve(widget.changeTheme(dark ? 'Dark' : 'Light', { disableUndo: true }))
      .then(() => {
        widget.applyOverrides?.(paneOverrides(dark));
        // The levels are re-drawn because a theme change can drop shapes, and a stop that vanishes when somebody
        // switches to dark mode is the worst possible thing for this particular overlay to do.
        const chart = chartOf(el);
        if (chart) {
          (el as unknown as { __pfEntities?: string[] }).__pfEntities = [];
          void drawOverlays(el, chart, overlay);
        }
      })
      .catch(() => {
        // A library edition without `changeTheme` keeps the theme it mounted with rather than losing the chart.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overlayKey is `overlay`'s identity
  }, [dark, overlayKey]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const widgetOf = (): TvWidget | null => (el as unknown as { _tv?: TvWidget })._tv ?? null;

    /** Saves the whole chart layout — drawings, studies and their settings, the timescale. */
    const persist = (): void => {
      const widget = widgetOf();
      if (!widget?.save) return;
      widget.save((state) => writeLayout(mode, state));
    };

    const onReady = (): void => {
      const widget = widgetOf();
      const saved = readLayout(mode);
      const afterRestore = (): void => {
        // The app owns *which instrument* is being traded, so the desk's selection is re-applied over whatever the
        // saved layout named. Everything else in the layout — drawings, indicators, the timescale — is the
        // trader's and is left exactly as they left it.
        const chart = chartOf(el);
        if (chart && widget?.setSymbol && chart.symbol?.() !== symbol) {
          widget.setSymbol(symbol, interval, () => {
            const next = chartOf(el);
            if (next) void drawOverlays(el, next, overlay);
          });
        } else if (chart) {
          void drawOverlays(el, chart, overlay);
        }
      };

      if (saved && widget?.load) {
        // A restored layout carries the trader's own visible range; overriding it with ours would undo the pan and
        // zoom they left the chart on.
        void Promise.resolve(widget.load(saved)).then(afterRestore, () => {
          showAvailableRange(el, symbol, interval);
          afterRestore();
        });
      } else {
        showAvailableRange(el, symbol, interval);
        afterRestore();
      }

      // Fires at most once every five seconds, on any change the trader could undo — a drawing added or moved, an
      // indicator added or removed or reconfigured, the interval changed.
      widget?.subscribe?.('onAutoSaveNeeded', persist);
      subscribed = Boolean(widget?.subscribe);
    };

    // ONLY unsubscribe what this effect actually subscribed. The library's `unsubscribe` reaches into a
    // per-event subscription bucket that exists only after a successful `subscribe`; calling it before the
    // ready event has fired (a re-render racing initialisation — an order landing, StrictMode's probe mount)
    // throws `reading 'unsubscribe' of undefined` INSIDE the library and took the whole screen to the error
    // boundary (AUD-001's visible half). The flag is the root fix; the catch is the boundary guard for a
    // third-party lifecycle that cannot be queried — a widget destroyed mid-frame may still throw, and losing
    // one auto-save hook must not cost the trader the application.
    let subscribed = false;
    el.addEventListener('pf-chart-ready', onReady);
    return () => {
      el.removeEventListener('pf-chart-ready', onReady);
      if (subscribed) {
        try {
          widgetOf()?.unsubscribe?.('onAutoSaveNeeded', persist);
        } catch {
          /* the widget died between the check and the call — the subscription died with it */
        }
      }
      // One last save on the way out: the five-second throttle means the last few seconds of work would otherwise
      // be the part that is lost, which is exactly the part they just did.
      persist();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `overlay` is rebuilt every render; `overlayKey` is its identity
  }, [symbol, interval, overlayKey, mode]);

  // Redraw when the desk moves a level, without waiting for another chart-ready that will not come.
  useEffect(() => {
    const el = ref.current;
    const widget = el ? (el as unknown as { _tv?: TvWidget; _ready?: boolean })._tv : null;
    const ready = el ? (el as unknown as { _ready?: boolean })._ready : false;
    if (!el || !widget || !ready) return;
    const chart = widget.activeChart?.() ?? widget.chart?.();
    if (chart) void drawOverlays(el, chart, overlay);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [overlayKey]);

  // Two different attributions, and only one of them is off.
  //
  //   · **TradingView's own, built into the widget** — the logo in the chart pane and the branding in its menus.
  //     ON, and deliberately left alone: the package disables no logo feature, and nothing here adds one. This is
  //     the attribution their Advanced Charts licence is actually about, and it travels with the widget.
  //
  //   · **The package's hand-rolled bar** — a `<div>` under the chart with a "Charts by TradingView" link, written
  //     by the design's author rather than by TradingView. OFF at the owner's instruction (2026-08-23), through the
  //     element's own documented `attribution="off"` switch rather than by editing the verbatim file.
  //
  // If the built-in attribution ever needs suppressing, that is a licence question before it is a code change.
  const attrs = {
    // Absolute, because this app is a router and the element's default (`charting_library/`) is relative to the
    // current URL: on `/execute/tradehub` the widget asked for `/execute/charting_library/bundles/…` and got four
    // 404s and a blank chart. The reference is a single file at a fixed path, so a relative default works there and
    // nowhere else.
    'library-path': '/charting_library/',
    attribution: 'off',
    ...rest,
  };
  return (
    <CustomElement
      tag="pf-tv-chart"
      elementRef={ref}
      attrs={attrs}
      {...(style ? { style } : {})}
      {...(className ? { className } : {})}
    />
  );
}

/** Tag → wrapper, for the transpiler's `x-import` emitter. */
export const CUSTOM_ELEMENTS: Record<string, string> = { 'pf-tv-chart': 'PfTvChart' };
